import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  fadeIn,
  fadeOut,
  slideInHorizontal,
  slideInVertical,
  slideOutHorizontal,
  slideOutVertical,
} from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { Location } from '@freelancer/location';
import { Pwa, SwipeNavigationService } from '@freelancer/pwa';
import { TimeUtils } from '@freelancer/time-utils';
import { IconModule, IconSize } from '@freelancer/ui/icon';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import type { Subscription } from 'rxjs';
import { SubscriptionLike } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StickyModule } from '../sticky/sticky.module';
import { TESTING_ANIMATION_DISABLED } from '../ui.config';
import {
  BottomDrawerSize,
  DrawerPosition,
  DrawerWidthSize,
} from './drawer.types';
import { ViewHeaderComponent } from '../view-header/view-header.component';

@Component({
  standalone: true,
  selector: 'fl-drawer',
  imports: [
    DragDropModule,
    IconModule,
    NgTemplateOutlet,
    ScrollingModule,
    StickyModule,
    ViewHeaderComponent,
  ],
  template: `
    <div
      #drawerContainerEl
      class="DrawerContainer"
      cdkScrollable
      [attr.data-position]="drawerPosition"
    >
      @if (!isBottomDrawer) {
        <div
          class="DrawerDialog"
          [attr.data-full-width]="fullWidth"
          [attr.data-size]="sizeDesktop"
          [@paneAnimationHorizontal]="{
            value: state,
            params: { distance: hideStatePosition },
          }"
          (@paneAnimationHorizontal.done)="animationDone($event)"
        >
          <ng-container *ngTemplateOutlet="drawerContent"></ng-container>
        </div>
      }
      @if (isBottomDrawer) {
        <div
          class="DrawerDialog desktop:hide"
          [attr.data-full-width]="fullWidth"
          [attr.data-bottom-drawer-size]="bottomDrawerSize"
          [@paneAnimationVertical]="{
            value: state,
            params: { distance: hideStatePosition },
          }"
          (@paneAnimationVertical.done)="animationDone($event)"
        >
          <ng-container *ngTemplateOutlet="drawerContent"></ng-container>
        </div>
      }

      <ng-template #drawerContent>
        <div
          #drawerContentDrag
          class="DrawerContent"
          i18n-cdkDragLockAxis
          cdkDrag
          [cdkDragDisabled]="!(drawerPosition === DrawerPosition.BOTTOM)"
          cdkDragLockAxis="y"
          (cdkDragStarted)="onDragStarted($event)"
          (cdkDragMoved)="onDragMoved($event)"
          (cdkDragEnded)="onDragEnded($event)"
          [attr.data-edge-to-edge]="edgeToEdge"
          [attr.data-full-width]="fullWidth"
          (swiperight)="handleSwipeRight()"
          (swipeleft)="handleSwipeLeft()"
          (swipebottom)="handleSwipeBottom()"
        >
          @if (!hideCloseButton) {
            <fl-icon
              class="DrawerCloseButton"
              i18n-label="Close drawer button label"
              label="Close drawer"
              [name]="'ui-close'"
              [hoverAnimation]="'spin-and-fade'"
              [size]="IconSize.MID"
              [class.mobile:hide]="fullWidth"
              (click)="handleEscape()"
              [useIconFont]="true"
            ></fl-icon>
          }
          @if (fullWidth) {
            <div class="DrawerHeaderContainer mobile:show">
              <fl-view-header
                class="DrawerHeader"
                [flSticky]="true"
                [flStickyContext]="'drawer'"
                [flStickyOrder]="0"
                [flStickyBehaviour]="StickyBehaviour.ALWAYS"
                [flStickyPosition]="StickyPosition.TOP"
                [heading]="mobileHeaderTitle"
              ></fl-view-header>
            </div>
          }
          @if (isOpen) {
            <div
              class="DrawerContent-inner"
              [attr.data-edge-to-edge]="edgeToEdge"
            >
              @if (drawerPosition === DrawerPosition.BOTTOM) {
                <div class="Handler"></div>
              }
              <ng-template [ngTemplateOutlet]="drawerTemplate"></ng-template>
            </div>
          }
        </div>
      </ng-template>
      <div
        class="DrawerBackdrop"
        aria-label="Drawer backdrop"
        i18n-aria-label="Drawer backdrop"
        [attr.data-backdrop]="isOpen"
        [attr.data-full-width]="fullWidth"
        [attr.tabindex]="0"
        [@backdropAnimation]="state"
        (click)="handleBackdropClick()"
      ></div>
    </div>
  `,
  styleUrls: ['./drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('paneAnimationHorizontal', [
      transition('* => visible', [
        useAnimation(slideInHorizontal, {
          params: { duration: '100', translateX: '{{ distance }}' },
        }),
      ]),
      transition('* => hidden', [
        useAnimation(slideOutHorizontal, {
          params: { duration: '100', translateX: '{{ distance }}' },
        }),
      ]),
    ]),
    trigger('paneAnimationVertical', [
      transition('* => visible', [
        useAnimation(slideInVertical, {
          params: { duration: '100', translateY: '{{ distance }}' },
        }),
      ]),
      transition('* => hidden', [
        useAnimation(slideOutVertical, {
          params: { duration: '100', translateY: '{{ distance }}' },
        }),
      ]),
    ]),
    trigger('backdropAnimation', [
      transition('visible => hidden', [
        useAnimation(fadeOut, { params: { duration: '100' } }),
      ]),
      transition('hidden => visible', [
        useAnimation(fadeIn, { params: { duration: '100' } }),
      ]),
    ]),
  ],
})
export class DrawerComponent implements OnDestroy, OnInit {
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private location = inject(Location);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);
  private swipeNavigation = inject(SwipeNavigationService);
  private pwa = inject(Pwa);
  private document = inject<Document>(DOCUMENT);
  private hostElementRef = inject(ElementRef);
  readonly testingCloseAnimationDisabled? = inject(TESTING_ANIMATION_DISABLED, {
    optional: true,
  });

  @ViewChild('drawerContentDrag') drawerContent: ElementRef;
  @ViewChild('drawerContainerEl') drawerContainerEl: ElementRef;

  drawerHeight: number;
  yDiff: number;
  IconSize = IconSize;
  DrawerPosition = DrawerPosition;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  canRemoveOverflow = false;
  state = 'hidden';
  hideStatePosition = '10%';
  drawerPosition = DrawerPosition.RIGHT;
  isBottomDrawer = false;

  @Input() bottomDrawerSize = BottomDrawerSize.MID;
  @Input() closeOnBackdropClick = true;
  @Input() edgeToEdge = false;
  @Input() hideCloseButton = false;
  @Input() mobileHeaderTitle: string;

  /** Defines the width of the drawer on desktop */
  @Input() sizeDesktop: DrawerWidthSize = 'fixed';

  @Input() set position(value: DrawerPosition) {
    this.drawerPosition = value;
    this.setHidePosition();
  }

  // Shows backdrop regardless of viewport width when set to false
  @Input() fullWidth = true;

  @Output() closeDrawer = new EventEmitter<void>();
  @Output() openDrawer = new EventEmitter<void>();

  @ContentChild('drawerTemplate')
  drawerTemplate: TemplateRef<any>;

  @HostBinding('class.IsActive') isOpen = false;

  private routeChangeSubscription?: Subscription;
  @RepetitiveSubscription()
  private drawerPopCloseSubscription?: SubscriptionLike;

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.close();
  }

  ngOnInit(): void {
    this.routeChangeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this._close({ fromRouteChange: true });
      });

    this.isBottomDrawer = this.drawerPosition === DrawerPosition.BOTTOM;
  }

  onDragStarted(event: any): void {
    this.drawerHeight = parseInt(
      window.getComputedStyle(this.drawerContent.nativeElement).height,
      10,
    );
    this.drawerContent.nativeElement.style.transition = 'none';
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  onDragMoved(event: any): void {
    event.source._dragRef.reset();
    this.yDiff = event.distance.y;
    if (this.yDiff >= 0) {
      const newHeight = this.drawerHeight - this.yDiff;
      this.drawerContent.nativeElement.style.height = `${newHeight}px`;
    }
  }

  onDragEnded(event: any): void {
    this.drawerContent.nativeElement.style.transition = 'height 0.1s ease-out';

    if (this.yDiff > 10) {
      this.close();

      this.timeUtils.setTimeout(() => {
        this.drawerContent.nativeElement.style.height = `${this.drawerHeight}px`;
      }, 100);
      this.renderer.removeStyle(document.body, 'overflow');
    } else {
      this.drawerContent.nativeElement.style.height = `${this.drawerHeight}px`;
    }
  }

  close({ fromRouteChange = false }: { fromRouteChange?: boolean } = {}): void {
    this._close({ fromRouteChange });
  }

  private _close({
    fromRouteChange = false,
  }: { fromRouteChange?: boolean } = {}): void {
    if (this.isOpen) {
      this.state = 'hidden';
      this.setHidePosition();
      this.changeDetectorRef.markForCheck();

      if (this.testingCloseAnimationDisabled) {
        /**
         * Don't wait for `animationDone` callback which is being triggered later than expected in
         * consecutive UI tests. This effectively removes the close animation of the drawer.
         */
        this.isOpen = false;
        this.closeDrawer.emit();
        this.togglePageScrollState();
        this.moveContainerToHost();
      }

      if (this.drawerPopCloseSubscription) {
        this.drawerPopCloseSubscription.unsubscribe();
      }
      if (!fromRouteChange) {
        this.location.back();
      }
      setTimeout(() => {
        this.swipeNavigation.enableSwipeGestures(true);
      });
    }
  }

  setHidePosition(): void {
    this.hideStatePosition =
      this.drawerPosition === DrawerPosition.LEFT ? '-10%' : '10%';
  }

  handleBackdropClick(): void {
    if (this.closeOnBackdropClick) {
      this.close();
    }
  }

  open(): void {
    if (!this.isOpen) {
      this.swipeNavigation.enableSwipeGestures(false);
      this.isOpen = true;
      this.moveContainerToBody();
      this.state = 'visible';
      this.openDrawer.emit();
      this.togglePageScrollState();
      this.drawerPopCloseSubscription = this.location._createBackButtonState({
        onPop: () => this._close({ fromRouteChange: true }),
      });
      this.changeDetectorRef.markForCheck();
    }
  }

  togglePageScrollState(): void {
    const target = this.document.body;
    const currentOverflow = target.style.overflow;

    // We only want to remove overflow when drawer component is the
    // one that set it. For instance, when drawer is placed within a modal
    // overflow is already set and we don't want to remove it
    // once drawer is closed
    if (this.isOpen && !currentOverflow) {
      this.canRemoveOverflow = true;
    }
    if (this.isOpen) {
      this.disableBodyScroll();
    } else if (!this.isOpen && this.canRemoveOverflow) {
      this.enableBodyScroll();
      this.canRemoveOverflow = false;
    }
  }

  disableBodyScroll(): void {
    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
  }

  enableBodyScroll(): void {
    this.renderer.removeStyle(this.document.body, 'overflow');
  }

  animationDone(event: AnimationEvent): void {
    if (event.fromState === 'visible' && event.toState === 'hidden') {
      this.isOpen = false;
      this.closeDrawer.emit();
      this.togglePageScrollState();
      this.moveContainerToHost();
    }
  }

  handleSwipeRight(): void {
    if (
      this.pwa.isInstalled() &&
      this.drawerPosition === DrawerPosition.RIGHT
    ) {
      this.close();
    }
  }

  handleSwipeLeft(): void {
    if (this.pwa.isInstalled() && this.drawerPosition === DrawerPosition.LEFT) {
      this.close();
    }
  }

  handleSwipeBottom(): void {
    if (
      this.pwa.isInstalled() &&
      this.drawerPosition === DrawerPosition.BOTTOM
    ) {
      this.close();
    }
  }

  /**
   * Moves the drawer container to document.body so it renders outside any
   * intermediate stacking context. This prevents the bottom drawer from
   * appearing beneath fixed-position elements like the primary navigation.
   */
  private moveContainerToBody(): void {
    if (this.isBottomDrawer && this.drawerContainerEl?.nativeElement) {
      this.renderer.appendChild(
        this.document.body,
        this.drawerContainerEl.nativeElement,
      );
    }
  }

  private moveContainerToHost(): void {
    if (this.isBottomDrawer && this.drawerContainerEl?.nativeElement) {
      this.renderer.appendChild(
        this.hostElementRef.nativeElement,
        this.drawerContainerEl.nativeElement,
      );
    }
  }

  ngOnDestroy(): void {
    this.enableBodyScroll();
    this.moveContainerToHost();

    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }
  }
}
